import { useState, useEffect } from 'react'
import { Plus, Trash2, ChevronDown, ChevronRight, X, FileText, Loader2, Calendar, Edit3, Check } from 'lucide-react'
import { transcriptsApi, type DealTranscript } from '@api/client'

interface TranscriptsModalProps {
  dealId: number
  isOpen: boolean
  onClose: () => void
}

export function TranscriptsModal({ dealId, isOpen, onClose }: TranscriptsModalProps) {
  const [transcripts, setTranscripts] = useState<DealTranscript[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)

  // New transcript form
  const [newTitle, setNewTitle] = useState('')
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0])
  const [newText, setNewText] = useState('')

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDate, setEditDate] = useState('')
  const [editText, setEditText] = useState('')

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      transcriptsApi.list(dealId).then((res) => {
        if (res.success && res.data) setTranscripts(res.data)
      }).finally(() => setLoading(false))
    }
  }, [isOpen, dealId])

  const handleAdd = async () => {
    if (!newText.trim() || !newDate) return
    setSaving(true)
    const res = await transcriptsApi.create(dealId, {
      title: newTitle.trim() || `Call on ${newDate}`,
      callDate: newDate,
      transcript: newText.trim(),
    })
    if (res.success && res.data) {
      setTranscripts((prev) => [res.data!, ...prev])
      setAdding(false)
      setNewTitle('')
      setNewDate(new Date().toISOString().split('T')[0])
      setNewText('')
    }
    setSaving(false)
  }

  const handleUpdate = async (id: number) => {
    setSaving(true)
    const res = await transcriptsApi.update(dealId, id, {
      title: editTitle.trim(),
      callDate: editDate,
      transcript: editText.trim(),
    })
    if (res.success && res.data) {
      setTranscripts((prev) => prev.map((t) => (t.id === id ? res.data! : t)))
      setEditingId(null)
    }
    setSaving(false)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this transcript? This cannot be undone.')) return
    const res = await transcriptsApi.remove(dealId, id)
    if (res.success) {
      setTranscripts((prev) => prev.filter((t) => t.id !== id))
      if (expandedId === id) setExpandedId(null)
    }
  }

  const startEdit = (t: DealTranscript) => {
    setEditingId(t.id)
    setEditTitle(t.title)
    setEditDate(t.call_date)
    setEditText(t.transcript)
    setExpandedId(t.id)
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">Call Transcripts</span>
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] rounded">
                {transcripts.length} saved
              </span>
            </div>
            <div className="flex items-center gap-2">
              {!adding && (
                <button
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded-lg hover:bg-blue-500/20 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add transcript
                </button>
              )}
              <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 max-h-[calc(100vh-10rem)] overflow-y-auto space-y-3">
            <p className="text-xs text-[var(--text-secondary)]">
              Store call transcripts, Avoma summaries, and meeting notes here. They stay with the deal and can be used by AI Assist across Blueprint, Demo Prep, and other tools.
            </p>

            {/* Add form */}
            {adding && (
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Title (e.g. Discovery call with John)"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="flex-1 px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                  />
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-[var(--text-muted)]" />
                    <input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      className="px-2 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                    />
                  </div>
                </div>
                <textarea
                  placeholder="Paste the transcript, call notes, or meeting summary here..."
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y font-mono"
                  rows={10}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setAdding(false); setNewTitle(''); setNewText('') }}
                    className="px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={saving || !newText.trim() || !newDate}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Save transcript
                  </button>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
              </div>
            )}

            {/* Empty state */}
            {!loading && transcripts.length === 0 && !adding && (
              <div className="text-center py-8">
                <FileText className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-2" />
                <p className="text-sm text-[var(--text-muted)]">No transcripts yet</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">Add call transcripts so they can be reused across the deal.</p>
              </div>
            )}

            {/* Transcript list */}
            {!loading && transcripts.map((t) => {
              const isExpanded = expandedId === t.id
              const isEditing = editingId === t.id
              const wordCount = t.transcript.split(/\s+/).filter(Boolean).length
              const preview = t.transcript.slice(0, 150).replace(/\n/g, ' ')

              return (
                <div key={t.id} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : t.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--text-primary)] truncate">{t.title}</span>
                        <span className="text-[10px] text-[var(--text-muted)] shrink-0">{t.call_date}</span>
                        <span className="text-[10px] text-[var(--text-muted)] shrink-0">{wordCount.toLocaleString()} words</span>
                      </div>
                      {!isExpanded && (
                        <p className="text-[11px] text-[var(--text-muted)] truncate mt-0.5">{preview}...</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => isEditing ? setEditingId(null) : startEdit(t)}
                        className="p-1.5 text-[var(--text-muted)] hover:text-blue-400 transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                      {isEditing ? (
                        <div className="space-y-3 pt-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="flex-1 px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                            />
                            <input
                              type="date"
                              value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              className="px-2 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]"
                            />
                          </div>
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] resize-y font-mono"
                            rows={12}
                          />
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-[var(--text-secondary)]">
                              Cancel
                            </button>
                            <button
                              onClick={() => handleUpdate(t.id)}
                              disabled={saving}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg disabled:opacity-50"
                            >
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap font-mono pt-3 max-h-80 overflow-y-auto">{t.transcript}</pre>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

// Lightweight hook to fetch transcripts for use in other components (e.g. AI suggest modal)
export function useTranscripts(dealId: number | null) {
  const [transcripts, setTranscripts] = useState<DealTranscript[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!dealId) return
    setLoading(true)
    transcriptsApi.list(dealId).then((res) => {
      if (res.success && res.data) setTranscripts(res.data)
    }).finally(() => setLoading(false))
  }, [dealId])

  const refresh = () => {
    if (!dealId) return
    transcriptsApi.list(dealId).then((res) => {
      if (res.success && res.data) setTranscripts(res.data)
    })
  }

  return { transcripts, loading, refresh }
}
